/**
 * usePoints Hook
 * Manages viewer points state and operations
 * Note: This hook only works for viewers. For streamers, it will skip API calls
 * and return null points without errors.
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient, ApiError, SessionExpiredError } from '../lib/api-client';
import { useAuth } from '../contexts/AuthContext';

// SessionExpiredErrorのチェック（instanceof がモジュール間で動作しない場合のため）
function isSessionExpiredError(err: unknown): boolean {
    return err instanceof SessionExpiredError ||
           (err instanceof Error && err.name === 'SessionExpiredError');
}

export type ViewerStatus = 'waiting' | 'party' | 'peek';

export interface UsePointsReturn {
    points: number | null;
    isLoading: boolean;
    error: string | null;
    refreshBalance: () => Promise<void>;
    purchasePoints: (amount: 1000 | 3000 | 5000 | 10000 | 30000 | 50000) => Promise<void>;
    consumeForWatch: (streamKey: string, status?: ViewerStatus) => Promise<number>;
}

export function usePoints(): UsePointsReturn {
    const { isViewer } = useAuth();
    const [points, setPoints] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const refreshBalance = useCallback(async () => {
        // Skip API call for non-viewers (streamers, admins, managers)
        // They don't have a points balance
        if (!isViewer()) {
            setIsLoading(false);
            setPoints(null);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            const response = await apiClient.getPointBalance();
            setPoints(response.points);
        } catch (err) {
            // セッション切れの場合はエラー表示しない（リダイレクト処理済み）
            if (isSessionExpiredError(err)) {
                return;
            }
            if (err instanceof ApiError) {
                setError(err.detail);
            } else {
                setError('Failed to fetch point balance');
            }
        } finally {
            setIsLoading(false);
        }
    }, [isViewer]);

    const purchasePoints = useCallback(async (amount: 1000 | 3000 | 5000 | 10000 | 30000 | 50000) => {
        // Skip for non-viewers
        if (!isViewer()) {
            throw new Error('Points operations are only available for viewers');
        }

        let caughtError: Error | null = null;

        try {
            setIsLoading(true);
            setError(null);
            const response = await apiClient.purchasePoints(amount);
            setPoints(response.points);
        } catch (err) {
            caughtError = err as Error;
            // セッション切れの場合はエラー表示しない（リダイレクト処理済み）
            if (!isSessionExpiredError(err)) {
                if (err instanceof ApiError) {
                    setError(err.detail);
                } else {
                    setError('Failed to purchase points');
                }
            }
        } finally {
            setIsLoading(false);
        }

        // Throw after state updates are queued
        if (caughtError) {
            throw caughtError;
        }
    }, [isViewer]);

    const consumeForWatch = useCallback(async (streamKey: string, status: ViewerStatus = 'waiting') => {
        // Skip for non-viewers
        if (!isViewer()) {
            throw new Error('Points operations are only available for viewers');
        }

        try {
            setError(null);
            const response = await apiClient.consumePointsForWatch(streamKey, status);
            setPoints(response.points);
            return response.points;
        } catch (err) {
            // セッション切れの場合はエラー表示しない（リダイレクト処理済み）
            if (!isSessionExpiredError(err)) {
                if (err instanceof ApiError) {
                    setError(err.detail);
                } else {
                    setError('Failed to consume points for watch');
                }
            }
            throw err;
        }
    }, [isViewer]);

    // Load initial balance
    useEffect(() => {
        refreshBalance();
    }, [refreshBalance]);

    return {
        points,
        isLoading,
        error,
        refreshBalance,
        purchasePoints,
        consumeForWatch,
    };
}
